import React from 'react';
import RecoverableImage from '../images/RecoverableImage';
import Link from './BrowseLink';
import { useQuery } from '@tanstack/react-query';
import { playlistApi } from '../../api/playlist';
import PlaylistArtwork from '../../components/content/PlaylistArtwork';
import AppAuthor from '../../app-ui/AppAuthor';
import baseline, { appTextStyle, formatAppCount, playlistText } from '../../app-ui/baseline';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { playlistItemCount } from './playlistSelection';
export const playlistTo = (item) => `/lists/${encodeURIComponent(item.slug || item.id)}`;
function usePlaylistPreviewData(item, hydrate = false) {
  const images = [...new Set((item.previewImageUrls || []).filter(Boolean))];
  const custom = item.imageUrl?.trim();
  const slug = item.slug?.trim();
  const key = slug || item.id;
  const detail = useQuery({
    queryKey: ['browse-playlist-preview', slug ? 'slug' : 'id', key],
    queryFn: ({ signal }) => {
      const config = { signal, suppressGlobalError: true };
      return slug
        ? playlistApi.getPlaylistBySlug(slug, config)
        : playlistApi.getPlaylistById(item.id, config);
    },
    // Build60: ordinary artwork resolves only when empty; featured/winner fills up to four.
    enabled: Boolean(key) && !custom && (hydrate ? images.length < 4 : images.length === 0),
    retry: false,
    staleTime: 300000,
  });
  const data = detail.data?.data;
  const previewImageUrls = [
    ...new Set([
      ...images,
      ...[...(data?.albums || []), ...(data?.tracks || [])]
        .sort((a, b) => a.position - b.position)
        .map((entry) => entry.albumImageUrl)
        .filter(Boolean),
    ]),
  ].slice(0, 4);
  return { ...item, imageUrl: custom || data?.imageUrl?.trim(), previewImageUrls };
}
export function PlaylistPreview({ item, hydrate = false }) {
  const artwork = usePlaylistPreviewData(item, hydrate);
  return <PlaylistArtwork playlist={artwork} />;
}
export function PlaylistSaves({ count = 0 }) {
  const { locale } = useWebLocale();
  return (
    <span className="pp-playlist-saves" style={appTextStyle(baseline.contentText.metadata)}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        aria-hidden="true"
      >
        <path d="M6 3h12v18l-6-4-6 4Z" />
      </svg>
      {formatAppCount(count, locale)}
    </span>
  );
}
export default function BrowsePlaylistCard({
  item,
  rank,
  hero = false,
  curator = false,
  curatorCount = 0,
  curatorSaves = 0,
}) {
  const { locale } = useWebLocale();
  const text = (key, params, compact) => playlistText(locale, key, params, compact);
  const styles = baseline.playlist;
  const count = playlistItemCount(item);
  const metadata = text(item.type === 'ALBUM' ? 'albumItems' : 'trackItems', { count });
  const date = (
    <span className="pp-browse-playlist-meta" style={appTextStyle(baseline.contentText.metadata)}>
      {metadata}
      {item.createdAt ? ` · ${item.createdAt.slice(0, 10)}` : ''}
    </span>
  );
  const author = (
    <AppAuthor
      name={item.username || item.userNickname || 'postpop'}
      image={item.userProfileImageUrl}
      mark={item.userProfileMark}
    />
  );
  const artwork = usePlaylistPreviewData(item, hero || curator);
  const ambient = artwork.imageUrl || artwork.previewImageUrls?.[0];
  return (
    <Link
      to={playlistTo(item)}
      className={`pp-browse-playlist${hero ? ' pp-browse-playlist-hero' : ''}${curator ? ' pp-browse-playlist-curator' : ''}${rank ? ' pp-browse-playlist-ranked' : ''}`}
      aria-label={
        hero || rank ? text('popularRankA11y', { rank: rank || 1, title: item.title }) : undefined
      }
    >
      {hero && (
        <span className="pp-playlist-ambient" aria-hidden="true">
          {ambient && <RecoverableImage src={ambient} alt="" />}
        </span>
      )}
      {rank && !hero && (
        <span
          className="pp-playlist-rank"
          style={appTextStyle(styles.popularChartRank, baseline.contentText.label)}
        >
          {String(rank).padStart(2, '0')}
        </span>
      )}
      <PlaylistArtwork playlist={artwork} />
      <div
        className="pp-browse-playlist-copy"
        style={{
          gap: curator
            ? styles.heroCopy.gap
            : hero
              ? styles.popularWinnerCopy.gap
              : styles.playlistRowCopy.gap,
        }}
      >
        {curator && (
          <span
            className="pp-playlist-eyebrow"
            style={appTextStyle(styles.heroEyebrow, baseline.contentText.metadata)}
          >
            {text('featured')}
          </span>
        )}
        {hero && (
          <span
            className="pp-playlist-eyebrow"
            style={appTextStyle(styles.popularWinnerRank, baseline.contentText.label)}
          >
            01 · {text('mostPopular')}
          </span>
        )}
        <strong
          style={appTextStyle(
            curator
              ? styles.heroTitle
              : hero
                ? styles.popularWinnerTitle
                : rank
                  ? styles.popularChartTitle
                  : baseline.tokens.typography.albumTitle,
          )}
        >
          {item.title}
        </strong>
        {curator ? (
          <div style={{ marginTop: styles.heroOwnerRow.marginTop }}>
            <AppAuthor
              featured
              name={item.username || item.userNickname || 'postpop'}
              image={item.userProfileImageUrl}
              mark={item.userProfileMark}
              metadata={`${text('createdCount', { count: curatorCount }, true)} · ${text('totalSaves', { count: curatorSaves }, true)}`}
            />
          </div>
        ) : hero ? (
          <div
            className="pp-playlist-winner-identity"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: styles.popularWinnerIdentity.gap,
              marginTop: styles.popularWinnerIdentity.marginTop,
            }}
          >
            {author}
            <div className="pp-playlist-footer" style={{ gap: styles.popularWinnerFooter.gap }}>
              {date}
              <PlaylistSaves count={item.bookmarkCount} />
            </div>
          </div>
        ) : (
          <>
            {author}
            {date}
          </>
        )}
      </div>
      {!hero && !curator && <PlaylistSaves count={item.bookmarkCount} />}
    </Link>
  );
}
